// Pure logic for the Replacement Logs table: group service records into the
// request/pack tree the table renders, map the filter panel selection to API
// query params, and map a full record into the review-screen ("preview") shape.
// Unit-tested.

// Display form of a request id: the whole value, so it can be read off the screen
// and quoted without opening anything. It was briefly truncated to the first
// segment to save table width; showing it in full was the decision.
//
// A dash for an absent id, so a row predating the column does not render "undefined".
export const formatRequestID = (requestID) => (requestID || "").trim() || "-";

const labelFor = (list = [], code) => {
    const found = (list || []).find((o) => o.code === code);
    return found ? found.label : code;
};

// "3 packs" / "1 pack" for the request row's packCount column.
export const formatPackCount = (count = 0) =>
    `${count} ${count === 1 ? "pack" : "packs"}`;

// Every observation option across both warranty lists.
//
// Looked up across BOTH rather than picking one by warranty status: observations
// are answered per pack, so one request's packs can draw from different lists and
// a single-list lookup would render raw codes.
const allObservationOptions = (config = {}) => [
    ...(config.observations?.inWarranty || []),
    ...(config.observations?.outOfWarranty || []),
];

const joinReasons = (reasons = []) =>
    (reasons || [])
        .map((r) => (typeof r === "string" ? r : r?.label || r?.code))
        .filter(Boolean)
        .join(", ");

// Blank values for the pack-level columns, used on a request row. A request can
// hold several packs, so any single value here would have to pick one of them
// arbitrarily -- the packs are one expand away instead.
// The serials are left empty rather than set to "-": their column carries
// defaultValue "-" and the shared text-with-copy renderer prints that for an empty
// value, so the dash still appears -- without a copy button beside it offering to
// copy a placeholder.
const PACK_COLUMNS_BLANK = {
    pack: "-",
    string: "-",
    outgoingSerial: "",
    incomingSerial: "",
    observation: "-",
    outgoingWarranty: "-",
};

// Blank values for the request-level columns, used on a pack row. The request row
// directly above already carries them; repeating them down every child would say
// the same thing N times.
const REQUEST_COLUMNS_BLANK = {
    requestID: "",
    packCount: "-",
    recordedDate: null,
    problemReported: "-",
    recordedBy: "-",
    odometer: null,
    busHeldUp: null,
    workStart: null,
    workEnd: null,
};

// The pack-level cells for one pack. Shared by the child row of a multi-pack
// request and the single row of a one-pack request, so both read identically.
const packLevelFields = (record, pack = {}, config = {}) => {
    const sd = record?.serviceDetails || {};
    // Warranty is moving from the record to the pack; read the pack's own value
    // and fall back to the record's so requests written either side of that
    // migration both display correctly. Same fallback as mapRecordToReviewState.
    const warrantyStatus = pack?.warrantyStatus || sd.warrantyStatus || "";
    const observationOptions = allObservationOptions(config);

    return {
        pack: pack?.pack || "-",
        string: pack?.string || "-",
        outgoingSerial: pack?.outgoingSerial || "",
        incomingSerial: pack?.incomingSerial || "",
        observation:
            (pack?.observations || [])
                .map((code) => labelFor(observationOptions, code))
                .filter(Boolean)
                .join(", ") || "-",
        outgoingWarranty: warrantyStatus
            ? labelFor(config.warrantyOptions, warrantyStatus)
            : "-",
    };
};

// One pack of one request -> one child table row. `config` resolves codes to
// labels for the Observation / warranty columns.
//
// `index` only backs the row key for a record written before absPackNo was stored.
export const flattenPackRow = (record, pack = {}, config = {}, index = 0) => ({
    // Deliberately no `id`: the table's rowKey is `record.id || record.key`, so
    // reusing the parent's id here would make two rows share one key.
    key: `${record?.id}-${pack?.absPackNo ?? index}`,
    _isPackRow: true,
    ...REQUEST_COLUMNS_BLANK,
    ...packLevelFields(record, pack, config),
});

// Service records -> the request/pack tree the table renders: one row per request,
// expanding to one row per pack.
//
// EVERY request with packs expands, a one-pack request included. Its pack row is
// the only place the pack, string and serials appear, so the tree reads the same
// way whatever the pack count and no request is a special case.
//
// This is also what replaced reading packReplacementDetails[0], which silently hid
// every pack after the first on any multi-pack request.
export const groupServiceRecordRows = (records = [], config = {}) =>
    (records || []).map((record) => {
        const sd = record?.serviceDetails || {};
        const packs = record?.packReplacementDetails || [];
        // reasons are replicated onto every pack on write, so the request-level
        // answer can be read back off any one of them.
        const first = packs[0] || {};

        return {
            id: record?.id,
            ...PACK_COLUMNS_BLANK,
            requestID: record?.replacementRequestUUID || "",
            packCount: formatPackCount(packs.length),
            recordedDate: sd.recordedAt || null,
            problemReported: joinReasons(first.reasons) || "-",
            recordedBy: sd.recordedByName || "-",
            odometer: sd.odometerKm ?? null,
            busHeldUp: sd.busHeldUpAt || null,
            workStart: sd.workStartAt || null,
            workEnd: sd.workEndAt || null,
            _documentsCount: (record?.documents || []).length,
            // Omitted rather than left empty for a request with no packs at all:
            // an empty `children` array still renders an expand control that opens
            // onto nothing.
            ...(packs.length > 0
                ? {
                      children: packs.map((pack, index) =>
                          flattenPackRow(record, pack, config, index)
                      ),
                  }
                : {}),
        };
    });

// Row keys of every request whose packs are worth opening on arrival. Used to
// auto-expand search results: the term may have matched a pack serial, and the
// matching pack is a child row, so a collapsed result would not show what matched.
export const getExpandableRowKeys = (rows = []) =>
    (rows || []).filter((row) => row?.children?.length).map((row) => row.id);

// Filter panel selection -> service-records GET query params. String/reason/
// date are documented params; pack/recordedBy are best-effort (see the table's
// note) and sent so the backend can honour them if/when supported.
export const mapSelectedFiltersToParams = (selectedFilters = {}) => {
    const params = {};
    if (selectedFilters.pack?.length) params.pack = selectedFilters.pack;
    if (selectedFilters.stringNo?.length) params.stringNo = selectedFilters.stringNo;
    if (selectedFilters.reason?.length) params.reason = selectedFilters.reason;
    if (selectedFilters.recordedBy?.length) params.recordedBy = selectedFilters.recordedBy;

    const dateRaw = selectedFilters.recordedDate?.[0];
    if (dateRaw) {
        try {
            const parsed = JSON.parse(dateRaw);
            const from = parsed.startDateUTC || parsed.startDate;
            const to = parsed.endDateUTC || parsed.endDate;
            if (from) params.recordedFrom = from;
            if (to) params.recordedTo = to;
        } catch (e) {
            /* ignore malformed date filter */
        }
    }
    return params;
};

// Full record (§3.6) -> the props RecordReplacementReview consumes, so the
// "view" action shows the exact same layout the create flow reviews.
//
// Reads EVERY packReplacementDetails entry. This used to take [0] only, which
// silently hid every pack after the first on any multi-pack request.
export const mapRecordToReviewState = (record, config = {}) => {
    const sd = record?.serviceDetails || {};
    const packDetails = record?.packReplacementDetails || [];
    // reasons are replicated onto every pack on write, so the request-level answer
    // can be read back off any one of them. observations are NOT: they are answered
    // per pack and are read from each pack below.
    const first = packDetails[0] || {};

    const predefined = new Set((config.problemsReported || []).map((o) => o.code));
    const reasons = first.reasons || [];
    const problemsReported = reasons
        .filter((r) => predefined.has(r?.code))
        .map((r) => r.code);
    const customProblems = reasons
        .filter((r) => !predefined.has(r?.code))
        .map((r) => (typeof r === "string" ? r : r?.label || r?.code))
        .filter(Boolean);

    const documents = {};
    (record?.documents || []).forEach((d) => {
        documents[d.docType] = { documentID: d.documentID, fileName: d.fileName };
    });

    const formState = {
        problemsReported,
        customProblems,
        packs: packDetails.map((p, index) => ({
            // absPackNo is the stable slot; index is only a fallback for a record
            // written before absPackNo was stored.
            key: String(p.absPackNo ?? index),
            absPackNo: p.absPackNo ?? null,
            stringLabel: p.string || "",
            packLabel: p.pack || "",
            outgoing: {
                serial: p.outgoingSerial || "",
                installedOn: p.installedOn || null,
                // Warranty is moving from the record to the pack; read the pack's own
                // value and fall back to the record's so records written either side
                // of that migration both display correctly.
                warrantyStatus: p.warrantyStatus || sd.warrantyStatus || "",
                observations: p.observations || [],
            },
            incoming: {
                serial: p.incomingSerial || "",
                manufacturingDate: p.incomingManufacturingDate || null,
                grade: p.packGrade || "",
                condition: p.incomingCondition || "",
            },
        })),
        timeline: {
            busHeldUpAt: sd.busHeldUpAt || null,
            workStartAt: sd.workStartAt || null,
            workEndAt: sd.workEndAt || null,
        },
        documents,
        remarks: sd.remarks || "",
    };

    return {
        formState,
        preview: record?.metricsSnapshot || null,
        requestID: record?.replacementRequestUUID || "",
    };
};
